import { GkvRoutingSkipReason } from './gkv-routing-skip-reason.enum.js';
/**
 * Why a §302 Abrechnungsfall is set aside by the Abrechnungslauf instead of
 * being billed (TM-9538 / #1514).
 *
 * **The selection is a closed list of eight.** Kostenträgerkennung fehlt ·
 * Routing unauflösbar · Geburtsdatum fehlt · Verordnungsdatum fehlt ·
 * Position nicht bepreisbar · Blankoverordnung · Leitsymptomatik fehlt ·
 * Leistungen vor Beginn der Abrechnungsbeauftragung — those eight decide
 * whether a Verordnung is *looked at*, and no ninth joins them.
 *
 * ⚠ **The eighth is the first that is not a verdict on the Verordnung**
 * (TM-9635 / #1619). The other seven say something is missing or forbidden on
 * the paper; {@link GkvBillingSkipReason.SERVICES_BEFORE_BEAUFTRAGUNG} says the
 * Beleg belongs to the Abrechnungsweg the practice used before it commissioned
 * Therame. Nothing is wrong with such a Fall and nobody can repair it — which
 * is why it is asked first and reported alone.
 *
 * ⚠ **It was six until #1547, and the seventh is not a loosening of that
 * rule.** `LEITSYMPTOMATIK_FEHLT` joined them because `ZHE` gave a captured
 * field a Pflicht it did not have before: the Anlage makes the Freitext
 * mandatory *„falls im Feld ‚Leitsymptomatik' der Wert ‚0000' übertragen
 * wird"*, so a Verordnung with neither a Häkchen nor a Freitext cannot be
 * written at all. The other six are unchanged.
 * {@link GkvBillingSkipReason.COPAYMENT_DEADLINE_PENDING} sits beside rather
 * than among them: it is only askable **after** the Zuzahlung of the Fall has
 * been derived (#1516), so it cannot be a selection rule.
 *
 * ⚠ **The last three are not selection rules either, and the closed list of
 * eight is unchanged by them.** `ABGEWAEHLT`, `ABGEBROCHEN` and `VERWAIST` are
 * outcomes of a *run* (#1519), not verdicts on a Verordnung: no rule produces
 * them, a human or a sweeper does. They live in this enum rather than in a
 * second one so that one item column answers *'why did this Fall not arrive at
 * the Kasse'* completely — two enums on two columns is how the two
 * halves of that answer drift apart. Everything else a reviewer
 * might call "incomplete" is billed with a note — see
 * {@link GkvBillingCandidateNote}. The recurring mistake across the §302
 * tickets has been *strictness*: three recommendations were rejected as
 * stricter than the rule, twice a proposed "skip". **When in doubt, bill.**
 *
 * ⚠ **The gate lives here, never on the table.** Every column of
 * `pt_gkv_insurance_data` is nullable and stays nullable, deliberately: capture
 * is soft, billing is hard. A `NOT NULL` there would block a Therapeut:in from
 * saving a Verordnung whose card they have not finished reading.
 *
 * Same shape as `DunningRunSkipReason`, `InvoiceRunSkipReason` and
 * `GkvRoutingSkipReason` — flat, German string literals, because the value is
 * what lands verbatim in the per-row tooltip.
 */
export var GkvBillingSkipReason;
(function (GkvBillingSkipReason) {
    /**
     * The eGK / Muster 13 card IK is not on the plan, so there is nothing to
     * resolve an address from. `SLLA.FKT` and `UNB.S003 Empfänger` both descend
     * from it; without it the Fall has no recipient at all.
     */
    GkvBillingSkipReason["KOSTENTRAEGER_CODE_MISSING"] = "kostentr\u00E4gerkennung fehlt";
    /**
     * The card IK is there, but the Kostenträgerdatei corpus does not answer.
     * The five ways that happens are {@link GkvRoutingSkipReason}'s; they are
     * folded into one reason here because the reviewer's next action is the same
     * for all five — the per-case `detail` carries which one it was.
     */
    GkvBillingSkipReason["ROUTING_UNAUFLOESBAR"] = "routing unaufl\u00F6sbar";
    /** `User.birthdate` is unset. `INV.Geburtsdatum` is a required field. */
    GkvBillingSkipReason["GEBURTSDATUM_FEHLT"] = "geburtsdatum fehlt";
    /**
     * `PrescriptionData.prescriptionDate` is unset. It is the Verordnungsdatum
     * `SLLA` transmits and the date every price, LEGS and Katalog release is
     * resolved against — no substitute is available and none may be invented.
     */
    GkvBillingSkipReason["VERORDNUNG_DATE_MISSING"] = "verordnungsdatum fehlt";
    /**
     * At least one Abrechnungsposition of this Verordnung carries no billable
     * amount.
     *
     * ⚠ **A typed skip, never a substituted price.** No fallback to the newest
     * release, none to the Höchstpreis, and no practice-typed GKV price. §302
     * prices are the published ones or they are nothing.
     *
     * Reached in this slice by a Verordnung that names no Heilmittelbereich
     * (Muster 13 Feld 16): the price lineage is `(Bereich, Vertrag) → Linie` with
     * no fallback across lineages, so without a Bereich there is no line to price
     * against. The catalogue-driven half arrives with #1516.
     */
    GkvBillingSkipReason["POSITION_NICHT_BEPREISBAR"] = "position nicht bepreisbar";
    /**
     * `PrescriptionData.isBlankVerordnung === true`.
     *
     * § 125a is out of v1 as a product concept (#1240) — the therapist, not the
     * prescriber, determines Heilmittel, Menge and Frequenz, and none of that is
     * modelled. The boolean is the guard, and it is the sole live defence against
     * a ≥3× under-bill on Ergotherapie.
     */
    GkvBillingSkipReason["BLANKOVERORDNUNG"] = "blankoverordnung";
    /**
     * The Verordnung carries neither a ticked Leitsymptomatik nor a
     * patientenindividuelle one in free text (TM-9539 / #1547).
     *
     * ⚠ **A `K`-Feld that another Feld's value makes mandatory.** A1 V21
     * §5.5.3.3, S. 71 first says *„Wenn kein Kreuz gesetzt ist, ist ‚0000' zu
     * übermitteln"* — and then makes the *Patientenindividuelle Leitsymptomatik*
     * *„zwingend anzugeben … falls im Feld ‚Leitsymptomatik' der Wert ‚0000'
     * übertragen wird"*. So the empty case is representable in `ZHE` only
     * together with a free text, and a Fall carrying neither has no legal
     * spelling in the file at all.
     *
     * ⚠ **Reachable on two paths, and neither is a defect at capture.** A
     * Diagnosegruppe chosen without ticking a Leitsymptomatik and without typing
     * one; or no Diagnosegruppe at all, in which case the whole block does not
     * render. **Lenient at capture, strict at billing** — the same
     * shape as `GEBURTSDATUM_FEHLT` and `VERORDNUNG_DATE_MISSING`, and for the
     * same reason: a Therapeut:in must be able to save a Verordnung they have not
     * finished reading off the paper.
     */
    GkvBillingSkipReason["LEITSYMPTOMATIK_FEHLT"] = "leitsymptomatik fehlt";
    /**
     * At least one Leistungstag of this Verordnung lies before the
     * Abrechnungsbeauftragung's first covered day (TM-9635 / #1619, #1570).
     *
     * A practice coming from a Rechenzentrum has Belege lying there that the
     * Rechenzentrum submits. If this Fall went out through Therame as well, the
     * same Beleg would stand at the Kasse twice.
     *
     * ⚠ **The whole Fall, never a part.** A Verordnung is one Beleg and cannot be
     * split between two Abrechnungsstellen; a Fall with one Leistungstag before
     * and ten after stays out entirely. The rule itself stands in
     * `caseHasDeliveryDayBeforeBeauftragung`.
     *
     * ⚠ **Only under Versand.** Under Übergabe there is no changeover, so no
     * boundary either, and no Fall is put aside for it.
     *
     * ⚠ **There is nothing to fix here**, unlike with the other seven. The
     * `detail` therefore names the date and the previous billing route instead of
     * sending somebody to a field.
     */
    GkvBillingSkipReason["SERVICES_BEFORE_BEAUFTRAGUNG"] = "leistungen vor beginn der abrechnungsbeauftragung";
    /**
     * The Fall would have to claim Zuzahlungskennzeichen `2`, and the recorded,
     * dated schriftliche Zahlungsaufforderung it presupposes is not there.
     *
     * ⚠ **Not one of the eight selection blockers, and not the Zuzahlungs-Balken
     * either.** Those eight decide whether a Verordnung is *looked at*; this one
     * arrives with the amounts (#1516), because it is only askable once the
     * Zuzahlung of the Fall has been derived.
     *
     * `2` does not mean "did not pay" but "did not pay **despite a written
     * demand**", so claiming it without the demand on file asserts to the Kasse
     * that a document exists. §302 owns the *Soll* and TM-9443 the *Ist*: until
     * the Ist is recorded nothing requests `2` — `deriveCopaymentIndicator`
     * excludes it at the type level — so no Fall reaches this reason today. It is
     * implemented rather than deferred so that the day TM-9443 does request it,
     * the run holds the Fall back instead of guessing.
     *
     * ⚠ **Distinct from the Balken, and the distinction is the point.** The Balken
     * counts due, uncollected Zuzahlungen on Fälle that **are** billed — money the
     * practice deducts from its claim without holding it. A Fall set aside for
     * this reason is not billed, so it is not in that number.
     */
    GkvBillingSkipReason["COPAYMENT_DEADLINE_PENDING"] = "schriftliche zahlungsaufforderung fehlt";
    /**
     * A human took this Fall out of the selection before the run started
     * (TM-9538 / #1519).
     *
     * ⚠ **Recorded as an item rather than left out**, and that is the whole
     * reason it exists. A Fall that simply vanished from the run would be
     * indistinguishable afterwards from one a rule had refused — and *„eine
     * Regel hat es verworfen"* and *„ein Mensch hat es herausgenommen"* are the
     * two answers a reviewer most needs told apart when a Kasse asks why a
     * Verordnung never arrived.
     */
    GkvBillingSkipReason["ABGEWAEHLT"] = "abgew\u00E4hlt";
    /**
     * The run was canceled while this Fall was still waiting.
     *
     * Nothing was written for it, so it is free again and reappears as a
     * candidate in the next run. Its file, if it had one, is `SKIPPED` too and
     * its Anspruchszeilen are deleted — the run never reached a produced file.
     */
    GkvBillingSkipReason["ABGEBROCHEN"] = "abgebrochen";
    /**
     * The run was still holding this Fall when the worker read the candidates
     * again, and the fresh read no longer offers it (TM-9538 / #1521).
     *
     * Between the button and the worker a Verordnung can be billed on the private
     * side, lose its Kostenträgerkennung, or have its address stop resolving —
     * the Kostenträgerdatei editions turn over 15 to 25 times a year.
     *
     * ⚠ **A skip and not a failure, and the difference is who goes looking.**
     * Nothing went wrong here: the Fall is simply no longer this run's to send,
     * and it is free again for the next one. Reporting it as `FAILED` would send
     * somebody hunting a defect that does not exist — and the recurring §302
     * mistake is strictness. The per-case `detail` carries which of the three it
     * was.
     */
    GkvBillingSkipReason["NICHT_MEHR_ABRECHENBAR"] = "nicht mehr abrechenbar";
    /**
     * The run was left `IN_PROGRESS` past the orphan threshold and the sweeper
     * closed it (#1524).
     *
     * The sweeper-only counterpart of {@link GkvBillingSkipReason.ABGEBROCHEN}:
     * same consequence for the Fall, different author, and the audit trail says
     * which.
     */
    GkvBillingSkipReason["VERWAIST"] = "verwaist";
})(GkvBillingSkipReason || (GkvBillingSkipReason = {}));
/**
 * Every {@link GkvRoutingSkipReason}, folded onto the run's own reason.
 *
 * ⚠ **This direction, and not the other.** The run's enum is the flat one the
 * UI reads; routing's is a detail of one subsystem. `satisfies Record<…>` is
 * the single measure against drift: the day address resolution grows a sixth
 * reason, the type check breaks here rather than the new reason silently
 * falling through to "billable".
 */
export const GKV_BILLING_SKIP_REASON_BY_ROUTING_REASON = {
    [GkvRoutingSkipReason.KOSTENTRAEGER_UNBEKANNT]: GkvBillingSkipReason.ROUTING_UNAUFLOESBAR,
    [GkvRoutingSkipReason.NO_RECEIVING_POINT]: GkvBillingSkipReason.ROUTING_UNAUFLOESBAR,
    [GkvRoutingSkipReason.INSURER_TYPE_AMBIGUOUS]: GkvBillingSkipReason.ROUTING_UNAUFLOESBAR,
    [GkvRoutingSkipReason.ROUTING_ZYKLUS]: GkvBillingSkipReason.ROUTING_UNAUFLOESBAR,
    [GkvRoutingSkipReason.RECEIVING_POINT_AMBIGUOUS]: GkvBillingSkipReason.ROUTING_UNAUFLOESBAR,
};
//# sourceMappingURL=gkv-billing-skip-reason.enum.js.map